import React from 'react'
import { Box, Tooltip, makeStyles } from '@material-ui/core'
import PetsIcon from '@material-ui/icons/Pets'
import PeopleIcon from '@material-ui/icons/People'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import BlockIcon from '@material-ui/icons/Block'
import ReplayIcon from '@material-ui/icons/Replay'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'

import { PetHoverIcon } from '../../../components/PetHoverIcon'
import { OccupancyFacts, OccupancyLease } from './types'
import { getLeaseType, getLeaseTypeLabel } from './occupancyLease'

const TYPE_COLOR = {
    renewal: '#047857',
    transfer: '#b45309',
    new_move_in: '#3730a3',
}

const useStyles = makeStyles({
    factIcons: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
        color: '#6b7280',
    },
    factIcon: {
        fontSize: 14,
    },
})

export function OccupancyFactIcons({
    lease,
    compact,
}: {
    lease: OccupancyLease
    compact?: boolean
}) {
    const classes = useStyles()
    const facts: OccupancyFacts | null | undefined = lease.occupancy_facts
    const type = getLeaseType(lease)
    const mutualNames = (facts?.mutual_with || [])
        .map((peer) => peer.name)
        .filter(Boolean)
    const unresolved = (facts?.requested || []).filter(
        (row) => row.status !== 'mutual',
    )
    const iconSize = compact ? 'inherit' : 'small'
    const icons: Array<{
        key: string
        title: string
        color: string
        node: React.ReactNode
    }> = [
        {
            key: 'type',
            title: getLeaseTypeLabel(type),
            color: TYPE_COLOR[type],
            node:
                type === 'renewal' ? (
                    <ReplayIcon
                        className={classes.factIcon}
                        fontSize={iconSize}
                    />
                ) : type === 'transfer' ? (
                    <SwapHorizIcon
                        className={classes.factIcon}
                        fontSize={iconSize}
                    />
                ) : (
                    <PersonOutlineIcon
                        className={classes.factIcon}
                        fontSize={iconSize}
                    />
                ),
        },
    ]
    if (mutualNames.length) {
        icons.push({
            key: 'mutual',
            title: `Requested roommate (matched): ${mutualNames.join(', ')}`,
            color: '#6b7280',
            node: (
                <PeopleIcon className={classes.factIcon} fontSize={iconSize} />
            ),
        })
    }
    if (unresolved.length) {
        const lines = unresolved.map((row) => {
            const bits = [row.name, row.email, row.phone].filter(Boolean)
            return bits.join(' · ')
        })
        icons.push({
            key: 'requested',
            title: `Requested roommate (not matched):\n${lines.join('\n')}`,
            color: '#6b7280',
            node: (
                <PersonAddIcon
                    className={classes.factIcon}
                    fontSize={iconSize}
                />
            ),
        })
    }
    if (facts?.comfortable_with_roommate_pets === false) {
        icons.push({
            key: 'no-pets',
            title: 'Not okay living with a roommate who has a pet',
            color: '#6b7280',
            node: (
                <BlockIcon className={classes.factIcon} fontSize={iconSize} />
            ),
        })
    }

    return (
        <Box
            className={classes.factIcons}
            onPointerDown={(event) => event.stopPropagation()}
        >
            {icons.map((icon) => (
                <Tooltip
                    key={icon.key}
                    title={
                        <span style={{ whiteSpace: 'pre-line' }}>
                            {icon.title}
                        </span>
                    }
                >
                    <span style={{ display: 'inline-flex', color: icon.color }}>
                        {icon.node}
                    </span>
                </Tooltip>
            ))}
            {facts?.has_pet &&
                (facts.pets && facts.pets.length > 0 ? (
                    <PetHoverIcon
                        pets={facts.pets}
                        className={classes.factIcon}
                        fontSize={iconSize}
                        htmlColor="#6b7280"
                    />
                ) : (
                    <Tooltip title="Has a pet">
                        <span
                            style={{ display: 'inline-flex', color: '#6b7280' }}
                        >
                            <PetsIcon
                                className={classes.factIcon}
                                fontSize={iconSize}
                            />
                        </span>
                    </Tooltip>
                ))}
        </Box>
    )
}
